import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowSquareOutIcon } from '@yonderland/andes-icons';
import { BottomSheet, Box, Breakpoint, Link, Modal, useDialogContext } from '@yonderland/andes-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { generateTestId } from '../../../util/TestUtil';
import Garan from './assets/Garan';
const WARRANTY_DETAILS_TEST_ID = 'warranty-details';
const DetailsContent = ({ brand, productCode, warrantyYears, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.product);
    return (_jsx(Box, { paddingBlock: "6", children: _jsx(Garan, { brand: brand, productCode: productCode, warrantyYears: warrantyYears, ariaLabel: t('pdp.warranty.details.aria', { brand, productCode, years: warrantyYears }), testId: WARRANTY_DETAILS_TEST_ID }) }));
};
const ReadMoreLink = ({ onClick }) => {
    const { t } = useTranslation(I18N_NAMESPACES.product);
    return (_jsx(Link, { theme: "neutral", variant: "secondary", onClick: onClick, iconRight: { source: ArrowSquareOutIcon, size: 'sm' }, testId: generateTestId({ testId: 'warranty-label', suffix: 'link' }), ariaLabel: t('pdp.warranty.readmore'), "aria-haspopup": "dialog", children: t('pdp.warranty.readmore') }));
};
const ModalTrigger = ({ onReadMoreClick }) => {
    const { open, isOpen } = useDialogContext();
    // Workaround for Cotopaxi Popover (type=DRAWER) attaching a global keyup listener on
    // document.body that closes the PDP tabs when Escape is pressed — even when this modal is open.
    // Intercepting in capture phase prevents the event from ever reaching that handler.
    // Remove once ProductDetailTabPopover migrates from Cotopaxi Popover to Andes Sidebar,
    // which uses a proper focus-trap and won't need a global body listener.
    useEffect(() => {
        if (!isOpen)
            return;
        const stopEscapePropagation = (e) => {
            if (e.key === 'Escape')
                e.stopPropagation();
        };
        document.addEventListener('keyup', stopEscapePropagation, true);
        return () => document.removeEventListener('keyup', stopEscapePropagation, true);
    }, [isOpen]);
    const handleClick = (event) => {
        event.stopPropagation();
        open();
        onReadMoreClick === null || onReadMoreClick === void 0 ? void 0 : onReadMoreClick();
    };
    return _jsx(ReadMoreLink, { onClick: handleClick });
};
const MobileWarrantyDetails = ({ brand, productCode, warrantyYears, onReadMoreClick, }) => {
    const [isOpen, setIsOpen] = useState(false);
    // Same workaround as ModalTrigger above: intercept Escape in capture phase to prevent
    // the Cotopaxi Drawer's global body listener from closing the PDP tabs while the
    // BottomSheet is open. Also closes the BottomSheet itself on Escape.
    // Remove once ProductDetailTabPopover migrates to Andes Sidebar.
    useEffect(() => {
        if (!isOpen)
            return;
        const handleEscapeKeyUp = (e) => {
            if (e.key !== 'Escape')
                return;
            e.stopPropagation();
            setIsOpen(false);
        };
        document.addEventListener('keyup', handleEscapeKeyUp, true);
        return () => document.removeEventListener('keyup', handleEscapeKeyUp, true);
    }, [isOpen]);
    return (_jsxs(_Fragment, { children: [_jsx(ReadMoreLink, { onClick: event => {
                    event.stopPropagation();
                    setIsOpen(true);
                    onReadMoreClick === null || onReadMoreClick === void 0 ? void 0 : onReadMoreClick();
                } }), isOpen && (_jsx(BottomSheet, { defaultOpen: true, onClose: () => setIsOpen(false), hasCloseBtn: true, padding: "none", testId: generateTestId({ testId: WARRANTY_DETAILS_TEST_ID, suffix: 'bottom-sheet' }), children: _jsx(DetailsContent, { brand: brand, productCode: productCode, warrantyYears: warrantyYears }) }))] }));
};
export const WarrantyDetails = ({ brand, productCode, warrantyYears, onReadMoreClick, }) => (_jsxs(_Fragment, { children: [_jsx(Breakpoint, { until: "md", children: _jsx(MobileWarrantyDetails, { brand: brand, productCode: productCode, warrantyYears: warrantyYears, onReadMoreClick: onReadMoreClick }) }), _jsx(Breakpoint, { from: "md", children: _jsxs(Modal, { hasCloseBtn: true, width: { lg: '580px' }, testId: generateTestId({ testId: WARRANTY_DETAILS_TEST_ID, suffix: 'modal' }), children: [_jsx(Modal.Trigger, { children: _jsx(ModalTrigger, { onReadMoreClick: onReadMoreClick }) }), _jsx(Modal.Content, { children: _jsx(DetailsContent, { brand: brand, productCode: productCode, warrantyYears: warrantyYears }) })] }) })] }));
